import React, { useState, useEffect, useRef } from 'react';
import {
  Box,
  Typography,
  FormControl,
  Select,
  MenuItem,
  Button,
  useTheme,
} from '@mui/material';
import { X } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import RecordingIndicator from '../controls/RecordingIcon';

const MobileRecording = ({
  onClose,
  recording,
  startRecording,
  stopRecording,
  recordingDuration,
  wordCount,
  promptTemplates,
  selectedPrompt,
  setSelectedPrompt,
  audioDuration = 0,
}) => {
  const { t } = useTranslation();
  const [continuousDuration, setContinuousDuration] = useState(0);
  const durationIntervalRef = useRef(null);
  const theme = useTheme();

  // Format time for display (00:07:23)
  const formatTime = (seconds) => {
    const hours = Math.floor(seconds / 3600);
    const minutes = Math.floor((seconds % 3600) / 60);
    const secs = seconds % 60;

    return `${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
  };

  // Start counting duration when recording starts
  useEffect(() => {
    if (recording) {
      // Start from the current audioDuration
      setContinuousDuration(audioDuration);

      // Clear any existing interval
      if (durationIntervalRef.current) {
        clearInterval(durationIntervalRef.current);
      }

      // Set up a new interval to increment the duration every second
      durationIntervalRef.current = setInterval(() => {
        setContinuousDuration((prev) => prev + 1);
      }, 1000);
    } else {
      // Stop the interval when recording stops
      if (durationIntervalRef.current) {
        clearInterval(durationIntervalRef.current);
        durationIntervalRef.current = null;
      }
    }

    // Clean up the interval when component unmounts
    return () => {
      if (durationIntervalRef.current) {
        clearInterval(durationIntervalRef.current);
      }
    };
  }, [recording, audioDuration]);

  // Pre-select the first prompt template if none is selected and templates are available
  useEffect(() => {
    if (
      (!selectedPrompt || selectedPrompt === '') &&
      promptTemplates &&
      promptTemplates.length > 0
    ) {
      setSelectedPrompt(promptTemplates[0].id);
    }
  }, [promptTemplates, selectedPrompt, setSelectedPrompt]);

  const handleRecordingToggle = () => {
    if (recording) {
      stopRecording();
    } else {
      startRecording();
    }
  };

  const handleClose = () => {
    if (recording) {
      stopRecording();
    }
    onClose();
  };

  return (
    <Box
      sx={{
        position: 'fixed',
        bottom: 0,
        left: 0,
        right: 0,
        backgroundColor: 'background.paper',
        borderTopLeftRadius: '12px',
        borderTopRightRadius: '12px',
        padding: 2.5,
        paddingBottom: 3,
        zIndex: 11,
        display: 'flex',
        flexDirection: 'column',
        boxShadow: '0px -5px 16px 0px rgba(0, 0, 0, 0.15)',
      }}
    >
      {/* Header with close button */}
      <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 1 }}>
        <X size={24} onClick={handleClose} style={{ cursor: 'pointer' }} />
      </Box>

      {/* Recording info */}
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          mb: 3,
        }}
      >
        {/* RecordingIndicator component with click handler */}
        <Box
          sx={{
            mb: 3,
          }}
        >
          <RecordingIndicator
            recording={recording}
            outerSize={116}
            innerSize={96}
            iconSize={'56px'}
          />
        </Box>

        {/* Recording time */}
        <Box sx={{ textAlign: 'center', mb: 2 }}>
          <Typography
            variant='h4'
            sx={{
              fontFamily: 'Geist Mono',
              fontSize: 32,
              fontWeight: 400,
            }}
          >
            {formatTime(
              recording
                ? continuousDuration
                : audioDuration || recordingDuration
            )}
          </Typography>
          <Typography variant='body2' sx={{ color: 'text.secondary', mt: 1 }}>
            {t('recording.recordingTime')}
          </Typography>
        </Box>

        {/* Word count */}
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            mb: 1,
          }}
        >
          <Typography
            sx={{ fontFamily: 'Geist Mono', fontSize: 24, fontWeight: 300 }}
          >
            {wordCount}
          </Typography>
          <Typography variant='body2' sx={{ color: 'text.secondary' }}>
            {t('recording.words')}
          </Typography>
        </Box>
      </Box>

      {/* Prompt template selector and recording button */}
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
        <FormControl fullWidth size='small'>
          <Select
            value={selectedPrompt}
            onChange={(e) => setSelectedPrompt(e.target.value)}
            disabled={recording}
            sx={{
              height: 40,
              fontSize: 14,
              '& .MuiOutlinedInput-notchedOutline': {
                borderColor: '#E4E4E7',
              },
            }}
            MenuProps={{
              PaperProps: {
                sx: {
                  maxHeight: 300,
                },
              },
            }}
          >
            {promptTemplates &&
              promptTemplates.map((template) => (
                <MenuItem key={template.id} value={template.id}>
                  {template.template_name}
                </MenuItem>
              ))}
          </Select>
        </FormControl>

        <Button
          variant='buttonStandard'
          onClick={handleRecordingToggle}
          sx={{
            height: 40,
            fontSize: 14,
            bgcolor: recording ? theme.palette.secondary.main : '#18181B',
            '&:hover': {
              bgcolor: recording ? theme.palette.secondary.main : '#2D2D35',
            },
          }}
        >
          {recording
            ? t('recording.stopRecording')
            : t('recording.resumeRecording')}
        </Button>
      </Box>
    </Box>
  );
};

export default MobileRecording;
